import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { t, useTranslation } from "@redotech/redo-i18n";
import {
  Address,
  ReturnTotalCalculationsOutput,
} from "@redotech/redo-model/draft-return/draft-return";
import { hasReturnItemsThatRequireShipping } from "@redotech/redo-model/draft-return/util";
import { getResource } from "@redotech/redo-model/localization/resource";
import { Channel } from "@redotech/redo-model/return";
import { filterTruthy } from "@redotech/util/array";
import { isValidEmail } from "@redotech/util/email";
import { useContext, useEffect, useState } from "react";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { ShippingEstimateContext } from "../../contexts/shipping-estimate";
import { Navigator, ReturnAddressType, RouteName } from "../../navigator";
import { CustomerPortalAppSettings } from "../../settings";
import {
  fieldRequiredError,
  getAustraliaStreetValidationErrors,
} from "../../utils/address-validation";
import { useShipmentMethod } from "../useShipmentMethod/use-shipment-method";

export interface AddressInfo {
  address?: Address;
  title: string;
  edit?: () => void;
  addressText1: string;
  addressText2?: string;
  addressText3?: string;
  addressText4?: string;
  addressText5?: string;
  type: ReturnAddressType;
}

interface UseAddressResponse {
  title: string;
  subtitle: string;
  addresses: AddressInfo[];
  loading: boolean;
  continueDisabled: boolean;
  pickupNotAvailable: boolean;
  submitting: boolean;
  onContinue: () => void;
}

function determineAddressTitle(
  hasNonGreenReturnItems: boolean,
  showNewItems: boolean,
) {
  if (hasNonGreenReturnItems) {
    return t("Returning items from:");
  }
  if (showNewItems) {
    return t("Shipping new items to:");
  }
  return t("Your address:");
}

export const useAddress = ({
  navigator,
  settings,
  channel,
}: {
  navigator: Navigator;
  settings: CustomerPortalAppSettings | undefined;
  channel: Channel;
}): UseAddressResponse => {
  const { t } = useTranslation();
  const client = useContext(RpcClientContext);
  const returnContext = useContext(ReturnContext);
  const [addresses, setAddresses] = useState<AddressInfo[]>([]);
  const [continueDisabled, setContinueDisabled] = useState<boolean>(true);

  const copyResource = getResource({ overrides: settings?.resourceOverride });
  const shippingEstimateContext = useContext(ShippingEstimateContext);
  const {
    shipmentMethodOptions,
    onSelectShipmentMethod,
    submitting,
    isLoadingShipmentOptions,
  } = useShipmentMethod(navigator, settings, channel, copyResource);

  const calculationsLoad = useLoad(async (): Promise<
    ReturnTotalCalculationsOutput | undefined
  > => {
    if (!client || !returnContext.draftReturn) {
      console.error(
        "AddressScreen.calculationsLoad: client or draftReturn is null",
      );
      return;
    }
    const { calculations } = await client.calculateReturnTotals({
      draftReturnId: returnContext.draftReturn._id,
    });
    console.log("AddressScreen.calculationsLoad: calculations", calculations);
    return calculations;
  }, [client, returnContext.draftReturn]);

  const pickupFeeLoad = useLoad(
    async (signal) => {
      if (!client || !returnContext.draftReturn) {
        return undefined;
      }
      // If no shipping is required, then skip all shipping methods
      if (!hasReturnItemsThatRequireShipping(returnContext.draftReturn)) {
        return undefined;
      }

      const shippingEstimates = await shippingEstimateContext.getShipments();

      const pickupFee =
        shippingEstimates?.length === 1 && shippingEstimates?.[0]?.pickupFee
          ? shippingEstimates[0].pickupFee
          : undefined;

      return pickupFee;
    },
    [client, returnContext.draftReturn],
  );

  const getAddressText4 = (address: Address | undefined | null) => {
    if (!address) {
      return undefined;
    }

    let addressText4 = `${filterTruthy([address.city, address.state]).join(", ")}`;
    if (address.zip) {
      addressText4 += ` ${address.zip}`;
    }

    return addressText4;
  };

  const getShippingAddressInfo = useHandler((): AddressInfo | undefined => {
    if (!calculationsLoad.value || !returnContext.draftReturn) {
      return undefined;
    }

    const shippingAddressTitle = determineAddressTitle(
      calculationsLoad.value.processed.hasNonGreenReturnItems,
      calculationsLoad.value.processed.showNewItems,
    );

    const shippingAddress = returnContext.draftReturn.customer.shippingAddress;
    if (shippingAddress) {
      shippingAddress.email = shippingAddress.email?.trim();
    }

    return {
      address: shippingAddress ?? undefined,
      title: shippingAddressTitle,
      addressText1: shippingAddress?.name || "",
      addressText2: shippingAddress?.street1,
      addressText3: shippingAddress?.street2 ?? undefined,
      addressText4: getAddressText4(shippingAddress),
      addressText5: shippingAddress?.country ?? undefined,
      type: ReturnAddressType.SHIPPING,
      edit: () =>
        navigator.navigate({
          name: RouteName.EDIT_ADDRESS,
          params: { type: ReturnAddressType.SHIPPING },
        }),
    };
  });

  const getReturnAddressInfo = useHandler((): AddressInfo | undefined => {
    if (!calculationsLoad.value || !returnContext.draftReturn) {
      return undefined;
    }
    const { hasNonGreenReturnItems, showNewItems } =
      calculationsLoad.value.processed;
    if (!hasNonGreenReturnItems || !showNewItems) {
      return undefined;
    }

    const newOrderAddress = returnContext.draftReturn.customer.newOrderAddress;

    return {
      address: newOrderAddress ?? undefined,
      title: t("Shipping new items to:"),
      addressText1: newOrderAddress?.name || t("Same as returning items from."),
      addressText2: newOrderAddress?.street1,
      addressText3: newOrderAddress?.street2 ?? undefined,
      addressText4: getAddressText4(newOrderAddress),
      addressText5: newOrderAddress?.country ?? undefined,
      type: ReturnAddressType.NEW_ORDER,
      edit: () =>
        navigator.navigate({
          name: RouteName.EDIT_ADDRESS,
          params: { type: ReturnAddressType.NEW_ORDER },
        }),
    };
  });

  useEffect(() => {
    if (!calculationsLoad.value || !returnContext.draftReturn) {
      return;
    }
    const addresses: AddressInfo[] = [];

    const shippingAddressInfo = getShippingAddressInfo();
    if (shippingAddressInfo) {
      addresses.push(shippingAddressInfo);
    }
    const returnAddressInfo = getReturnAddressInfo();
    if (returnAddressInfo) {
      addresses.push(returnAddressInfo);
    }

    const australiaStreetErrors = getAustraliaStreetValidationErrors(
      shippingAddressInfo?.address?.street1,
      shippingAddressInfo?.address?.street2,
      shippingAddressInfo?.address?.country,
    );

    const errors = {
      name: fieldRequiredError(shippingAddressInfo?.address?.name || ""),
      street1:
        fieldRequiredError(shippingAddressInfo?.address?.street1 || "") ||
        australiaStreetErrors.street1,
      street2: australiaStreetErrors.street2,
      city: fieldRequiredError(shippingAddressInfo?.address?.city || ""),
      zipPostal: fieldRequiredError(shippingAddressInfo?.address?.zip || ""),
      country: fieldRequiredError(shippingAddressInfo?.address?.country || ""),
      email: !isValidEmail(shippingAddressInfo?.address?.email || "")
        ? t("Invalid email")
        : undefined,
    };

    const isDisabled = Object.values(errors).some(
      (error) => error !== undefined,
    );

    setContinueDisabled(isDisabled);

    setAddresses(addresses);
    const { hasNonGreenReturnItems, showNewItems } =
      calculationsLoad.value.processed;
    const isMissingShippingAddress =
      !shippingAddressInfo && (hasNonGreenReturnItems || showNewItems);
    //Automatically navigate to edit address if shipping address is null or undefined and they need a shipping address.
    if (isMissingShippingAddress) {
      navigator.navigate({
        name: RouteName.EDIT_ADDRESS,
        params: { type: ReturnAddressType.SHIPPING },
      });
    }
  }, [
    calculationsLoad.value,
    getShippingAddressInfo,
    getReturnAddressInfo,
    returnContext.draftReturn,
    navigator,
    t,
  ]);

  const onContinue = useHandler(async () => {
    // Skip choose shipment method page if there is only one option
    if (shipmentMethodOptions?.length === 1) {
      onSelectShipmentMethod(shipmentMethodOptions[0]!.selection);
    } else {
      //We can't use getStepInReturn because it will just return address (since it has no way of currently knowing if this screen was seen). A better solution might be to use local storage for this specific draft return id.
      navigator.navigate({
        name: calculationsLoad.value?.processed.hasNonGreenReturnItems
          ? RouteName.SHIPMENT_METHOD
          : RouteName.REVIEW,
        params: undefined,
      });
    }
  });

  return {
    title: t("Confirm address"),
    subtitle: t("Make sure the information below is correct."),
    addresses,
    loading: !calculationsLoad.value || isLoadingShipmentOptions,
    continueDisabled,
    pickupNotAvailable: pickupFeeLoad.pending ? false : !pickupFeeLoad.value,
    submitting,
    onContinue,
  };
};
